import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Stack, Paper, Typography, Alert, Box } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'

import { WeeklyTotal } from '@/core/api/time-tracking'
import { formatDuration } from '@/features/time-tracking/lib/format'

type SessionsSummaryProps = {
  totals: WeeklyTotal[]
  openSessionCount: number
}

type KpiCardProps = {
  label: React.ReactNode
  value: string | number
  icon: React.ReactNode
  accentColor: string
}

function KpiCard({ label, value, icon, accentColor }: KpiCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        flex: 1,
        p: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'box-shadow 200ms ease, transform 200ms ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 46, 63, 0.08)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accentColor,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2.5}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${accentColor}14`,
            color: accentColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}

export function SessionsSummary({ totals, openSessionCount }: SessionsSummaryProps) {
  useLingui()

  const totalSeconds = totals.reduce((sum, t) => sum + t.totalSeconds, 0)
  const totalSessions = totals.reduce((sum, t) => sum + t.sessionCount, 0)

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <KpiCard
          label={<Trans>Total hours</Trans>}
          value={formatDuration(totalSeconds)}
          icon={<AccessTimeRoundedIcon sx={{ fontSize: 28 }} />}
          accentColor="#002e3f"
        />
        <KpiCard
          label={<Trans>Sessions</Trans>}
          value={totalSessions}
          icon={<EventNoteRoundedIcon sx={{ fontSize: 28 }} />}
          accentColor="#059669"
        />
      </Stack>
      {openSessionCount > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          icon={<WarningAmberRoundedIcon />}
          sx={{
            borderRadius: 2,
            '& .MuiAlert-message': { fontWeight: 500 },
          }}
        >
          <Trans>{openSessionCount} open session(s) excluded from totals.</Trans>
        </Alert>
      )}
    </Stack>
  )
}
